// MindSparkInsights.jsx
import React, { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";

const STAKEHOLDERS = [
  {
    id: "students",
    label: "Students & Self-Learners",
    emoji: "👩‍🎓",
    pains: [
      "Struggling to track daily reflections, moods, and study habits consistently.",
      "Lack of motivation without recognition for small wins and progress.",
      "Limited access to quick, engaging learning content like quizzes and current affairs.",
      "Difficulty in building problem-solving skills through structured analysis.",
      "Overwhelmed by academic and personal growth without personalized tools."
    ],
    opportunities: [
      "Daily Chronicle for logging thoughts, moods, and habits with auto-compiled Diary/Yearbook.",
      "Applause hub to celebrate wins and appreciate peers, boosting motivation.",
      "Brain Fuel Quizzes and Youth Knowledge for daily engaging learning bites.",
      "Root Cause Analysis (RCA) tool to sharpen problem-solving mindset.",
      "Focus Timer, AI Counselling, and Dashboard for personalized growth tracking."
    ]
  },
  {
    id: "families",
    label: "Families & Parents",
    emoji: "👨‍👩‍👧‍👦",
    pains: [
      "Hard to monitor children's academic and emotional progress holistically.",
      "Limited family-wide tools for shared celebrations and goal-setting.",
      "Challenges in fostering a supportive environment for self-reflection and learning.",
      "Lack of visibility into how digital tools impact family dynamics and growth."
    ],
    opportunities: [
      "Shared access to Report Card Challenge and Leaderboards for family progress tracking.",
      "Applause and Workshops for family bonding through celebrations and learning.",
      "My Digital Card and Yearbook for showcasing family achievements digitally.",
      "Nation Builder insights to align personal growth with family values."
    ]
  },
  {
    id: "mentors",
    label: "Mentors & Teachers",
    emoji: "👨‍🏫",
    pains: [
      "Difficulty providing scalable, personalized guidance to students.",
      "Limited platforms for hosting interactive sessions and tracking student engagement.",
      "Challenges in recognizing student efforts beyond traditional grading.",
      "Lack of tools to integrate AI and gamified elements into mentoring."
    ],
    opportunities: [
      "Expert Mentoring with pay-per-minute access and digital classrooms.",
      "Workshops & Podcasts for hosting sessions on studies, career, and self-growth.",
      "Integration with RCA and Quizzes to enhance teaching with analytical tools.",
      "MindSpark Games and Leaderboards to gamify mentoring and boost engagement."
    ]
  },
  {
    id: "peers",
    label: "Peers & Friends",
    emoji: "👭",
    pains: [
      "Missed opportunities for mutual appreciation and collaborative growth.",
      "Hard to stay connected for sharing reflections and wins digitally.",
      "Limited spaces for friendly competitions and collective inspiration.",
      "Challenges in discovering shared interests like creativity and mental health."
    ],
    opportunities: [
      "Applause to appreciate friends and celebrate collective wins.",
      "POV and Immersive Slides for sharing perspectives and visual stories.",
      "Leaderboard & Rewards for friendly contests like Marksheet Challenge.",
      "Shakti Forum-like communities for peer discussions and collaborations."
    ]
  },
  {
    id: "edngos",
    label: "Educational NGOs & Orgs",
    emoji: "🌱",
    pains: [
      "Struggles in measuring impact of youth development programs.",
      "Limited digital tools for scalable workshops and content distribution.",
      "Challenges in engaging youth with fun, rewarding learning experiences.",
      "Difficulty in providing 24/7 support beyond in-person sessions."
    ],
    opportunities: [
      "Partner with MindSpark for dashboards tracking engagement and outcomes.",
      "Host Workshops, Podcasts, and Games to scale youth programs digitally.",
      "Leverage AI Counselling and Quizzes for accessible, gamified learning.",
      "Creator Hub for youth to share content and build community initiatives."
    ]
  },
  {
    id: "edgov",
    label: "Education Authorities & Gov",
    emoji: "🏛️",
    pains: [
      "Gaps in real-time data for youth skill-building and mental health initiatives.",
      "Challenges scaling national programs like skill development digitally.",
      "Limited metrics for evaluating reflection, creativity, and engagement.",
      "Difficulty integrating AI and gamification into policy-driven education."
    ],
    opportunities: [
      "Dashboards with KPIs on growth, quizzes, and rewards for policy insights.",
      "Alignment with Nation Builder for youth contributing to national goals.",
      "Scale via Marketplace, Referrals, and Collectibles for incentivized learning.",
      "Integrate Focus Timer and RCA into school curricula for measurable impact."
    ]
  },
  {
    id: "edcorps",
    label: "EdTech Corporates & Sponsors",
    emoji: "🏢",
    pains: [
      "Hard to find platforms with integrated reflection, learning, and rewards.",
      "Limited ROI tracking for sponsored youth programs and content.",
      "Challenges in partnering for AI-driven, personalized education tools.",
      "Difficulty engaging youth beyond basic content delivery."
    ],
    opportunities: [
      "CSR integrations with Leaderboards, Rewards, and Marketplace for impact.",
      "Sponsor Workshops, Games, and Quizzes with branded analytics dashboards.",
      "Collaborate on AI Counselling and Creator Hub for innovative content.",
      "Track engagement via Yearbook and Applause for measurable sponsorship ROI."
    ]
  },
  {
    id: "creators",
    label: "Young Creators & Entrepreneurs",
    emoji: "🛍️",
    pains: [
      "Limited platforms to monetize writings, art, and digital creations.",
      "Challenges in building audience and earning from referrals or resells.",
      "Low visibility for products, services, and immersive content.",
      "Fragmented tools for tracking sales, ads, and customer loyalty."
    ],
    opportunities: [
      "Creator Hub (upcoming) for sharing and monetizing writings, videos, art.",
      "Referral & Ambassador Program with multi-level rewards for growth.",
      "Commerce Zone: Unity Deals, ThinkXpress, Nearby Deals, Products, Services.",
      "Rewarding Ads, Videos, and Games like Kharcho Jeeto for engagement."
    ]
  }
];

// Key USPs for MindSpark
const USPs = [
  {
    id: "dailyReflection",
    title: "Daily Reflection & Growth",
    desc: "Chronicle your thoughts, moods, and habits with RCA for clarity, auto-compiling into a personal Diary/Yearbook.",
    emoji: "📝",
  },
  {
    id: "celebrateWins",
    title: "Celebrate Wins & Stay Inspired",
    desc: "Applause for big/small victories, Brain Fuel Quizzes, Youth Knowledge, Leaderboards, Workshops, Report Card Challenges, and MindSpark Games.",
    emoji: "👏",
  },
  {
    id: "aiSupport",
    title: "AI & Expert Guidance",
    desc: "24/7 AI Buddy Counselling, Expert Mentoring, Handwriting Analysis for judgment-free support on studies, career, and stress.",
    emoji: "🤖",
  },
  {
    id: "productivityTools",
    title: "Productivity & Utilities",
    desc: "Focus Timer for Pomodoro sessions, EduEMI for education finance, Tonic for knowledge boosts, POV & Immersive Slides for social sharing.",
    emoji: "⏱️",
  },
  {
    id: "earningOpportunities",
    title: "Earning & Rewards",
    desc: "MindSpark Wallet, Referral/Resell programs, Rewarding Ads/Videos, Collectibles, Fun contests like Marksheet, Kharcho Jeeto, Aalu Dalo Sona Nikalo.",
    emoji: "💰",
  },
  {
    id: "commerceZone",
    title: "Commerce & Marketplace",
    desc: "Unity Deals, ThinkXpress, Nearby Deals, Products/Services/Rental/Wholesale, Sell via OrderFlow, Become Aggregator/Franchise.",
    emoji: "🛒",
  },
  {
    id: "communityRecognition",
    title: "Community & Recognition",
    desc: "My Digital Card, MindSpark Ranking/Winners, Immersive Magazine, BookShelf, Nation Builder for shared growth and badges.",
    emoji: "🏆",
  },
  {
    id: "creativeHub",
    title: "Creative & Analytics Hub",
    desc: "Upcoming Creator Hub for monetizing talent, Dashboard for visualizing progress, My Memories for reflection insights.",
    emoji: "📊",
  },
];

const Pill = ({ children }) => (
  <span className="inline-block bg-blue-700/30 text-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

export default function MindSparkInsights() {
  const [tab, setTab] = useState("stakeholders");
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStakeholders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-blue-950 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="mx-auto mb-6 text-center">
        <h1 className="text-2xl md:text-3xl font-bold text-blue-400">MindSpark Insights</h1>
        <p className="text-gray-300 mt-2 text-sm md:text-base">
          Your Daily Growth & Reflection Buddy – Empowering youth with learning, reflection, rewards, and community.
        </p>
      </header>
      {/* Tabs */}
      <nav className="grid grid-cols-2 md:grid-cols-2 gap-2 justify-center mb-4 place-items-center">
        <button
          onClick={() => setTab("stakeholders")}
          className={`flex items-center justify-center gap-1 px-4 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "stakeholders"
              ? "bg-blue-600 text-white shadow-lg"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          👩‍🎓 Stakeholders
        </button>
        <button
          onClick={() => setTab("usps")}
          className={`flex items-center justify-center gap-1 px-4 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "usps"
              ? "bg-blue-600 text-white shadow-lg"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          💡 USPs
        </button>
      </nav>
      {/* Search */}
      <div className="flex flex-col md:flex-row justify-center gap-2 mb-6 place-items-center">
        <input
          aria-label="Search stakeholders"
          placeholder="Search stakeholders, pain or opportunity..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-gray-800 text-gray-200 placeholder-gray-400 px-4 py-2 rounded-md text-sm w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          onClick={() => { setQuery(""); setExpandedId(null); }}
          className="text-sm px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors w-auto"
        >
          Reset
        </button>
      </div>
      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders */}
        {tab === "stakeholders" && (
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
            {filteredStakeholders.map((s) => {
              const isOpen = expandedId === s.id;
              return (
                <article key={s.id} className="bg-gradient-to-br from-gray-800 to-blue-900/30 rounded-xl border border-blue-800/30 shadow-lg overflow-hidden">
                  <div className="p-6 flex items-start gap-4">
                    <div className="text-4xl flex-shrink-0">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-xl font-semibold text-blue-300">{s.label}</h3>
                        <button
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                          className="bg-blue-700/30 hover:bg-blue-700/50 text-sm px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                        >
                          {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                        </button>
                      </div>
                      {!isOpen && (
                        <p className="text-sm text-gray-300">
                          <strong>Pain points:</strong> {s.pains.slice(0, 2).join(" • ")}
                        </p>
                      )}
                      {isOpen && (
                        <div className="space-y-4 text-sm text-gray-200">
                          <div>
                            <h4 className="font-semibold text-blue-300 mb-2">Pain Points</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {s.pains.map((p, idx) => <li key={idx}>{p}</li>)}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold text-blue-300 mb-2">Opportunities</h4>
                            <ul className="list-disc list-inside space-y-1">
                              {s.opportunities.map((o, idx) => <li key={idx}>{o}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {filteredStakeholders.length === 0 && (
              <div className="bg-gray-800 p-8 rounded-xl text-center text-gray-300 col-span-full">
                No results found for your search. Try adjusting your query!
              </div>
            )}
          </section>
        )}
        {/* USPs */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-blue-700/20 to-gray-800 rounded-xl p-6 border border-blue-600/20 hover:shadow-xl transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="text-3xl flex-shrink-0">{u.emoji}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-xl text-blue-300 mb-3">{u.title}</h3>
                    <p className="text-sm text-gray-200">{u.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}